'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { getProductUrl, type ProductHit } from '@libs/algolia';
import { getBuyItAgainGuest, getProductsByIds } from '@libs/api-proxy-client';
import type { BuyItAgainGuestApiResponse, BuyItAgainGuestPlacement } from '@libs/common-types';
import { getLocale, getVisitorId, logger } from '@libs/core';

const OPTEX_SEARCH_BIA_GUEST_COUNT = 20;
const OPTEX_SEARCH_BIA_GUEST_CALLER = 'web:searchbar:bia-guest';

export type OptexSearchBiaGuestProduct = {
    sku: string;
    recRank: number;
    name: string;
    imageUrl: string;
    href: string;
};

export function getOptexSearchBiaGuestPlacement(pathname: string): BuyItAgainGuestPlacement {
    return /(?:^|\/)cart\/?$/.test(pathname) ? 'incart' : 'homepage';
}

function mapOptexSearchBiaGuestProducts(
    recommendations: BuyItAgainGuestApiResponse,
    hits: ProductHit[]
): OptexSearchBiaGuestProduct[] {
    const optexHitsBySku = new Map(hits.filter(Boolean).map(hit => [hit.objectID, hit]));
    const optexProducts = recommendations.flatMap(recommendation => {
        const optexHit = optexHitsBySku.get(recommendation.sku_nbr);
        const optexImageUrl = optexHit?.images?.small ?? optexHit?.images?.large;

        if (!optexHit?.name || !optexHit.masterProductID || !optexImageUrl) return [];

        return [
            {
                sku: recommendation.sku_nbr,
                recRank: recommendation.rec_rank,
                name: optexHit.name,
                imageUrl: optexImageUrl,
                href: getProductUrl(optexHit),
            },
        ];
    });

    if (optexProducts.length < recommendations.length) {
        logger.warn(
            `[OPTEX SEARCH BIA GUEST][Warn]: Resolved ${optexProducts.length} of ${recommendations.length} recommendations; rendering resolved products`
        );
    }

    return optexProducts;
}

async function fetchOptexSearchBiaGuestRecommendations(
    placement: BuyItAgainGuestPlacement
): Promise<BuyItAgainGuestApiResponse> {
    try {
        return await getBuyItAgainGuest({ placement, count: OPTEX_SEARCH_BIA_GUEST_COUNT });
    } catch (error) {
        logger.error('[OPTEX SEARCH BIA GUEST][Error]: Recommendation request failed', error);
        return [];
    }
}

async function fetchOptexSearchBiaGuestProducts(
    recommendations: BuyItAgainGuestApiResponse
): Promise<OptexSearchBiaGuestProduct[]> {
    try {
        const hits = await getProductsByIds({
            ids: recommendations.map(recommendation => recommendation.sku_nbr),
            caller: OPTEX_SEARCH_BIA_GUEST_CALLER,
        });
        return mapOptexSearchBiaGuestProducts(recommendations, hits);
    } catch (error) {
        logger.error('[OPTEX SEARCH BIA GUEST][Error]: Product lookup failed', error);
        return [];
    }
}

export function useOptexSearchBiaGuest(isOptexSearchBiaGuestEnabled: boolean) {
    const optexPathname = usePathname() ?? '';
    const optexPlacement = getOptexSearchBiaGuestPlacement(optexPathname);
    const optexLocale = getLocale();
    const [optexVisitorId, setOptexVisitorId] = useState('');
    const [optexProducts, setOptexProducts] = useState<OptexSearchBiaGuestProduct[]>([]);

    useEffect(() => {
        setOptexVisitorId(isOptexSearchBiaGuestEnabled ? getVisitorId() : '');
    }, [isOptexSearchBiaGuestEnabled]);

    useEffect(() => {
        if (!isOptexSearchBiaGuestEnabled || !optexVisitorId) {
            setOptexProducts([]);
            return;
        }

        let isCancelled = false;

        async function fetchOptexSearchBiaGuest() {
            const recommendations = await fetchOptexSearchBiaGuestRecommendations(optexPlacement);
            if (isCancelled) return;

            if (recommendations.length === 0) {
                setOptexProducts([]);
                return;
            }

            const products = await fetchOptexSearchBiaGuestProducts(recommendations);
            if (!isCancelled) setOptexProducts(products);
        }

        void fetchOptexSearchBiaGuest();

        return () => {
            isCancelled = true;
        };
    }, [isOptexSearchBiaGuestEnabled, optexLocale, optexPlacement, optexVisitorId]);

    return {
        optexProducts,
    };
}
